import React from 'react';
import { Typography, Calendar } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BaseCard } from './BaseCard';
import { CalendarCardProps } from './CardProps';
import { ReturnsPopover } from 'components/Custom/ReturnsPopover';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import {
  formatCalendarMonthYear,
  getPickerLocale,
} from 'i18n/calendarLabels';
import dayjs from 'dayjs';

const { Text } = Typography;

export const CalendarCard: React.FC<CalendarCardProps> = ({
  cardId,
  calendarValue,
  setCalendarValue,
  calendarMode,
  onCalendarModeChange,
  getReturnsForDate,
  createSubmission,
}) => {
  const { t, i18n } = useTranslation();
  const pickerLocale = React.useMemo(
    () => getPickerLocale(t, i18n.language),
    [t, i18n.language]
  );

  // Apply calendar mode from preferences (month vs year view)
  React.useEffect(() => {
    const applyMode = () => {
      const calendarElement = document.querySelector(
        '.dashboard-calendar-wrapper .ant-picker-calendar'
      );
      if (!calendarElement) return;
      const yearPanel = calendarElement.querySelector(
        '.ant-picker-calendar-month-panel'
      );
      const datePanel = calendarElement.querySelector(
        '.ant-picker-calendar-date-panel'
      );
      if (yearPanel && datePanel) {
        if (calendarMode === 'month') {
          (yearPanel as HTMLElement).style.display = 'none';
          (datePanel as HTMLElement).style.display = 'block';
        } else {
          (yearPanel as HTMLElement).style.display = 'block';
          (datePanel as HTMLElement).style.display = 'none';
        }
      }
    };
    const timeoutId = setTimeout(applyMode, 100);
    return () => clearTimeout(timeoutId);
  }, [calendarMode]);

  return (
    <BaseCard
      cardId={cardId}
      bodyStyle={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
      }}
    >
      <style>
        {`
          .dashboard-calendar-wrapper * {
            text-shadow: none !important;
            -webkit-text-stroke: 0 !important;
          }
          .dashboard-calendar-wrapper .ant-picker-calendar-date,
          .dashboard-calendar-wrapper .ant-picker-calendar-date-content,
          .dashboard-calendar-wrapper .ant-picker-cell,
          .dashboard-calendar-wrapper .ant-picker-cell-inner,
          .dashboard-calendar-wrapper .ant-picker-calendar-date .ant-picker-cell-inner,
          .dashboard-calendar-wrapper .ant-picker-calendar-date-content .ant-picker-cell-inner,
          .dashboard-calendar-wrapper .ant-picker-cell-inner > *,
          .dashboard-calendar-wrapper .ant-picker-cell-inner span,
          .dashboard-calendar-wrapper .ant-picker-cell-inner div {
            text-shadow: none !important;
            -webkit-text-stroke: 0 !important;
            text-rendering: optimizeLegibility !important;
          }
          .dashboard-calendar-wrapper .ant-picker-cell-inner {
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
            font-smooth: always !important;
          }
          .dashboard-calendar-wrapper .ant-picker-calendar-date-content {
            position: relative !important;
          }
          .dashboard-calendar-wrapper .ant-picker-cell-inner::before,
          .dashboard-calendar-wrapper .ant-picker-cell-inner::after {
            display: none !important;
            content: none !important;
          }
          .dashboard-calendar-wrapper .ant-picker-cell-inner *::before,
          .dashboard-calendar-wrapper .ant-picker-cell-inner *::after {
            display: none !important;
            content: none !important;
          }
          .dashboard-calendar-wrapper .ant-picker-calendar-mode-switch {
            display: none !important;
          }
          .dashboard-calendar-wrapper .ant-picker-calendar-month-panel {
            display: none !important;
          }
          .dashboard-calendar-wrapper .ant-picker-content th {
            font-size: 11px;
            font-weight: 600;
            text-transform: none;
            padding: 4px 0;
          }
        `}
      </style>
      <div
        className="dashboard-calendar-wrapper"
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Calendar
          fullscreen={false}
          locale={pickerLocale}
          value={calendarValue}
          onChange={setCalendarValue}
          headerRender={({ value, onChange }) => {
            const monthName = formatCalendarMonthYear(
              value.month(),
              value.year(),
              t,
              i18n.language
            );
            const goToPreviousMonth = () => {
              const newValue = value.subtract(1, 'month');
              onChange(newValue);
              setCalendarValue(newValue);
            };
            const goToNextMonth = () => {
              const newValue = value.add(1, 'month');
              onChange(newValue);
              setCalendarValue(newValue);
            };

            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  marginBottom: '8px',
                }}
              >
                <LeftOutlined
                  onClick={goToPreviousMonth}
                  style={{
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#41AAAF',
                    padding: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-primary-hover, #64bbbd)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#41AAAF';
                  }}
                />
                <Text
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'var(--color-text-dark, #042a2a)',
                  }}
                >
                  {monthName}
                </Text>
                <RightOutlined
                  onClick={goToNextMonth}
                  style={{
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#41AAAF',
                    padding: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-primary-hover, #64bbbd)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#41AAAF';
                  }}
                />
              </div>
            );
          }}
          onPanelChange={(value, mode) => {
            setCalendarValue(value);
            // Persist calendar mode to user preferences (replaces localStorage)
            if (mode === 'month' || mode === 'year') {
              onCalendarModeChange(mode);
            }
          }}
          fullCellRender={(date) => {
            const returnsForDate = getReturnsForDate(dayjs(date));
            const dateNumber = dayjs(date).date();
            const isToday = dayjs(date).isSame(dayjs(), 'day');
            const isOtherMonth =
              dayjs(date).month() !== calendarValue.month();
            const hasDueDates = returnsForDate.length > 0;

            // Determine background color (CSS vars so High contrast can remaps)
            let backgroundColor = 'transparent';
            if (isToday) {
              backgroundColor = 'var(--color-primary)';
            } else if (hasDueDates) {
              backgroundColor = 'var(--color-primary-soft)';
            }

            const cellContent = (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '24px',
                  border:
                    hasDueDates && !isToday
                      ? '2px solid var(--color-primary)'
                      : 'none',
                  borderRadius: hasDueDates || isToday ? '4px' : '0',
                  backgroundColor: backgroundColor,
                }}
              >
                <span
                  style={{
                    textShadow: 'none',
                    WebkitTextStroke: '0',
                    color: isOtherMonth
                      ? 'var(--color-text-disabled)'
                      : isToday
                      ? 'var(--color-on-primary, #ffffff)'
                      : hasDueDates
                      ? 'var(--color-primary)'
                      : 'var(--color-text-base, #2e2e2e)',
                    fontSize: '14px',
                    lineHeight: '22px',
                    fontWeight: isToday || hasDueDates ? 600 : 400,
                  }}
                >
                  {formatDisplayInteger(dateNumber, i18n.language)}
                </span>
                {hasDueDates && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-primary)',
                      zIndex: 1,
                    }}
                  />
                )}
              </div>
            );

            if (returnsForDate.length === 0) {
              return cellContent;
            }

            return (
              <ReturnsPopover
                returns={returnsForDate}
                onClick={() => {}}
                onCreateSubmission={createSubmission}
              >
                {cellContent}
              </ReturnsPopover>
            );
          }}
        />
      </div>
    </BaseCard>
  );
};

